import { createApp } from 'vue'
import { prepareWebviewRecovery } from './utils/webviewRecovery'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import { setupErrorHandler } from './utils/errorHandler'
import { startLogFlush } from './utils/logger'
import { initSentry } from './utils/sentry'
import { useAuthStore } from './stores/auth'
import { isDesktopRuntime, platformEmit } from './platform/bridge'
import { startVersionCheck } from './utils/versionCheck'
import { startKnowledgePhraseChatPullScheduler } from './services/knowledgePhrase/KnowledgePhraseChatPullScheduler'
import SearchFilterBar from './components/common/list-layout/SearchFilterBar.vue'
import DataListTable from './components/common/list-layout/DataListTable.vue'
import './styles/tailwind.css'
import './styles.css'

let tokenExpiredHandling = false

function resolveTokenExpiredRedirect() {
  const routedPath = router.currentRoute.value.fullPath
  const hashPath = window.location.hash.replace(/^#/, '')
  const candidate = routedPath !== '/' ? routedPath : hashPath
  if (!candidate.startsWith('/') || candidate.startsWith('//') || candidate.startsWith('/login')) {
    return '/'
  }
  return candidate
}

/**
 * 统一接管无法刷新的登录态：立即清理旧会话并回到登录页，同时保留原访问地址。
 * 监听必须早于认证初始化，避免启动阶段的 401 事件在 App 挂载前丢失。
 */
function setupTokenExpiredHandler(authStore: ReturnType<typeof useAuthStore>) {
  window.addEventListener('token-expired', () => {
    if (tokenExpiredHandling) return

    const currentRoute = router.currentRoute.value
    if (currentRoute.path === '/login') {
      authStore.clearAuth()
      return
    }

    tokenExpiredHandling = true
    const redirect = resolveTokenExpiredRedirect()

    void authStore
      .expireSession('token_expired')
      .catch((error) => {
        console.warn('[Auth] expired session cleanup failed', error)
        authStore.clearAuth()
      })
      .then(() =>
        router.replace({
          path: '/login',
          query: { reason: 'expired', redirect },
        }),
      )
      .catch((error) => {
        console.warn('[Auth] redirect to login after token expiry failed', error)
        window.location.hash = '#/login?reason=expired'
      })
      .finally(() => {
        tokenExpiredHandling = false
      })
  })
}

/** Web 管理端启动序列：初始化权限会话后挂载后台管理路由。 */
async function bootstrap() {
  const app = createApp(App)
  initSentry(app, 'main')
  startLogFlush()

  const pinia = createPinia()
  app.use(pinia)
  app.component('SearchFilterBar', SearchFilterBar)
  app.component('DataListTable', DataListTable)

  const authStore = useAuthStore()
  authStore.setupTokenProvider()
  setupTokenExpiredHandler(authStore)
  const initController = new AbortController()
  const timeout = window.setTimeout(() => initController.abort('initFromIPC timeout'), 5000)
  try {
    await authStore.initFromIPC(initController.signal)
  } catch (error) {
    initController.abort(error)
  } finally {
    window.clearTimeout(timeout)
  }

  app.use(router)
  setupErrorHandler(app)
  startVersionCheck(router)
  await router.isReady()
  const recovery = await prepareWebviewRecovery(router)
  app.mount('#app')
  void recovery.ready()
  document.getElementById('static-splash')?.remove()
  document.getElementById('static-splash-style')?.remove()
  startDesktopHeartbeat('main')
  startKnowledgePhraseChatPullScheduler()
}

/**
 * 桌面端每 30 秒发送心跳。
 * 过渡期主窗口同时发送旧、新事件，兼容仍监听 main:heartbeat 的已发布客户端。
 */
function startDesktopHeartbeat(label: string) {
  if (!isDesktopRuntime) return

  const sendHeartbeat = () => {
    if (label === 'main') {
      platformEmit('main:heartbeat').catch(() => {
        // 旧协议发送失败不能阻断新版心跳；客户端也不得因心跳失败刷新页面。
      })
    }

    platformEmit('webview:heartbeat', { label }).catch(() => {
      // 主进程重启或 WebView 关闭时，下一轮心跳会重新尝试发送。
    })
  }

  sendHeartbeat()
  window.setInterval(sendHeartbeat, 30_000)
}

bootstrap()
